/**
 * /s/:token/f/:folderId — folder browse inside a share (Task 13, replacing the
 * Task 11 ShareFolderPage placeholder).
 *
 * Loads two things in parallel:
 *   - Landing (useShareLanding) — for adminName / share settings / expiry.
 *   - Folder contents (useShareFolder) — subfolders + videos.
 *
 * Breadcrumb only goes ONE level up (back to the share landing). Deeper
 * ancestor chains would require reading the ancestor walk from the server
 * (the guest endpoint only returns immediate children + the folder row) —
 * good enough for Frame.io parity in Phase 1.
 */

import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronRight, FileImage, Folder as FolderIcon, MessageSquare, Music2, Video as VideoIcon } from "lucide-react";
import { BrandMark } from "@/components/brand-mark";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { ShareIdentifyForm } from "@/components/shares/share-identify-form";
import { formatDurationChip } from "@/components/projects/video-card";
import { useShareFolder, useShareLanding } from "@/hooks/use-share";
import { AuthLoading } from "@/components/auth/auth-loading";
import { ApiError } from "@/lib/api";
import NotFoundPage from "@/pages/not-found";

function AssetIcon({ assetType, className }: { assetType?: string; className?: string }) {
  if (assetType === "audio") return <Music2 className={className} strokeWidth={1.5} />;
  if (assetType === "image") return <FileImage className={className} strokeWidth={1.5} />;
  return <VideoIcon className={className} strokeWidth={1.5} />;
}

export function ShareFolderPage() {
  const { token, folderId } = useParams<{ token: string; folderId: string }>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  const pv = preview ? "?preview=1" : "";   // Task 3 — keep team-preview on nav

  const landing = useShareLanding(token, preview);
  const folder = useShareFolder(token, folderId);

  // Loading — wait for landing so we know if identify is needed.
  if (landing.isLoading || folder.isLoading) return <AuthLoading />;

  // Token invalid / share removed — 404.
  const landingErr = landing.error as ApiError | undefined;
  if (landingErr?.status === 404) return <NotFoundPage />;
  if (folder.error && (folder.error as ApiError).status === 404) {
    return (
      <Shell>
        <div className="mx-auto max-w-md pt-32 text-center">
          <h2 className="font-display text-2xl font-bold tracking-tight">
            Folder not in this share
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            The folder might have moved, been deleted, or isn't part of this share.
          </p>
          <Button
            variant="outline"
            className="mt-6"
            onClick={() => navigate(`/s/${encodeURIComponent(token!)}${pv}`)}
          >
            Back to share
          </Button>
        </div>
      </Shell>
    );
  }

  if (!landing.data || !folder.data) {
    return (
      <Shell>
        <div className="mx-auto max-w-md pt-32 text-center">
          <h2 className="font-display text-2xl font-bold tracking-tight">
            Something went wrong
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Couldn't load this folder. Check your connection and try again.
          </p>
        </div>
      </Shell>
    );
  }

  // Multi-item share, no identify yet → gate on identify (same rule as landing).
  // The backend allows folder browse without identify, but Frame.io parity says
  // capture at entry for any multi-item share. Single-asset shares would have
  // auto-drilled to /v/... upstream so we never see them here.
  if (!landing.data.skipForm && landing.data.items.length > 1) {
    return (
      <Shell>
        <div className="mx-auto max-w-4xl px-6 py-16">
          <ShareIdentifyForm token={token!} adminName={landing.data.adminName} />
        </div>
      </Shell>
    );
  }

  const { folder: folderRow, folders: subfolders, videos } = folder.data;
  const empty = subfolders.length === 0 && videos.length === 0;

  return (
    <Shell>
      <main className="mx-auto max-w-6xl px-6 py-8">
        {/* Breadcrumb — one level up to the share landing */}
        <div className="mb-6 flex items-center gap-1.5 text-sm">
          <Link
            to={`/s/${encodeURIComponent(token!)}${pv}`}
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            {landing.data.share.name || "Share"}
          </Link>
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2} />
          <span className="font-display text-lg font-bold tracking-tight text-foreground">
            {folderRow.name}
          </span>
        </div>

        {empty && (
          <div className="rounded-xl border border-dashed border-border py-24 text-center text-sm text-muted-foreground">
            This folder is empty.
          </div>
        )}

        {subfolders.length > 0 && (
          <div className="mb-8">
            <p className="mb-3 text-sm text-muted-foreground">
              {subfolders.length} {subfolders.length === 1 ? "Folder" : "Folders"}
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {subfolders.map((sf) => (
                <Link
                  key={sf.id}
                  to={`/s/${encodeURIComponent(token!)}/f/${encodeURIComponent(sf.id)}${pv}`}
                  className="flex flex-col gap-2 rounded-xl border border-border bg-card p-3 transition-colors hover:bg-secondary/40"
                >
                  <div className="grid aspect-video place-items-center rounded-lg bg-secondary/60 text-muted-foreground">
                    <FolderIcon className="h-8 w-8" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">{sf.name}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {sf.folderCount + sf.videoCount === 0
                        ? "Empty"
                        : `${sf.folderCount ? `${sf.folderCount} folder${sf.folderCount === 1 ? "" : "s"}` : ""}${sf.folderCount && sf.videoCount ? " · " : ""}${sf.videoCount ? `${sf.videoCount} asset${sf.videoCount === 1 ? "" : "s"}` : ""}`}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {videos.length > 0 && (
          <div>
            <p className="mb-3 text-sm text-muted-foreground">
              {videos.length} {videos.length === 1 ? "Asset" : "Assets"}
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {videos.map((v) => {
                const durationText = formatDurationChip(v.durationSeconds, v.assetType);
                const commentCount = typeof v.commentCount === "number" ? v.commentCount : 0;
                const hasComments = commentCount > 0;
                return (
                <Link
                  key={v.id}
                  to={`/s/${encodeURIComponent(token!)}/v/${encodeURIComponent(v.id)}${pv}`}
                  className="flex flex-col gap-2 rounded-xl border border-border bg-card p-3 transition-colors hover:bg-secondary/40"
                >
                  <div className="relative grid aspect-video place-items-center overflow-hidden rounded-lg bg-secondary/60">
                    {v.assetType === "video" && v.cdnUrl ? (
                      <video src={v.cdnUrl} muted preload="metadata" className="h-full w-full object-cover" />
                    ) : v.assetType === "image" && v.cdnUrl ? (
                      <img src={v.cdnUrl} alt={v.title} loading="lazy" className="h-full w-full object-cover" />
                    ) : (
                      <AssetIcon assetType={v.assetType} className="h-7 w-7 text-muted-foreground" />
                    )}
                    {hasComments && (
                      <span
                        className="pointer-events-none absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 rounded-md bg-black/70 px-1.5 py-0.5 text-[11px] font-medium text-white"
                        title={`${commentCount} comment${commentCount === 1 ? "" : "s"}`}
                      >
                        <MessageSquare className="h-3 w-3" strokeWidth={2} />
                        <span className="tabular-nums">{commentCount}</span>
                      </span>
                    )}
                    {durationText && (
                      <span className="pointer-events-none absolute bottom-1.5 right-1.5 rounded-md bg-black/70 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-white">
                        {durationText}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">{v.title}</div>
                    {v.assetType && (
                      <div className="text-xs uppercase tracking-widest text-muted-foreground">
                        {v.assetType}
                      </div>
                    )}
                  </div>
                </Link>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="flex items-center justify-between border-b border-border px-4 py-3 md:px-8 md:py-4">
        <BrandMark />
        <ThemeToggle />
      </header>
      {children}
    </div>
  );
}
